import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';
import type { RootState } from '@swf/core/store';
import type {
  MeetingBasedMarketView,
  MeetingBasedRunner,
  RaceCardMarketTemplateConfig
} from '@swf/generated/types/api/sports';
import { Icon, Selection, StarRating, TimeFormVerdict } from '@swf/sports/components';
import { useMeetingMarketTemplatesWidget } from '@swf/sports/models/market-templates/meetingBased';
import { isGreyhoundRacing } from '@swf/sports/utils/isEventTypeId';

import {
  TXTCLI_More_Details,
  TXTWCL_Less_Detail,
  TXTWCL_Non_Runners
} from './RaceCardList.messages.json';
import styles from './RaceCardList.scss';
import HorseDetails from './components/HorseDetails/HorseDetails';
import JockeyAndTrainerDetails from './components/JockeyAndTrainerDetails/JockeyAndTrainerDetails';
import Silk from './components/Silk/Silk';

interface ListProps {
  market: MeetingBasedMarketView;
  maxNumberOfRunners?: number;
  templateConfig: RaceCardMarketTemplateConfig;
}

interface ListItemProps {
  runner: MeetingBasedRunner;
  market: MeetingBasedMarketView;
  raceCountryCode: string;
}

const RaceCardListItem: React.FC<ListItemProps> = ({ runner, market, raceCountryCode }) => {
  const locale = useSelector(select.locale.get);
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);

  if (!market.marketTime) return null;

  const { details, rating, daysOff, horse, form, ratingStars, comments } = runner;

  const shouldDisplayMoreDetailsButton =
    rating || daysOff || horse || details?.equipmentDescription || details?.weight || ratingStars;
  const shouldDisplayDetailsDropdown = shouldDisplayMoreDetailsButton || form;
  const isGreyhounds = isGreyhoundRacing(market.eventTypeId);

  return (
    <li className={styles.listItem}>
      <div className={styles.listItemTopRow}>
        <div className={styles.silk}>
          <Silk
            silkUrl={details?.silk}
            isGreyhounds={isGreyhounds}
            sortPriority={runner.sortPriority}
            raceCountryCode={raceCountryCode}
          />
        </div>
        <div
          className={classNames(styles.runnerInformation, {
            [styles.runnerCentralised]: isGreyhounds
          })}
        >
          <span>
            {details?.saddleCloth ? `${details.saddleCloth}. ` : null}
            {runner.runnerName}
          </span>
          <JockeyAndTrainerDetails details={runner.details} />
        </div>
        <div className={styles.btnContainer}>
          <Selection
            competitionId={market.competitionId}
            eventId={market.eventId}
            eventTypeId={market.eventTypeId}
            handicap={runner.handicap}
            isStartingPriceMarket={market.isStartingPriceMarket}
            key={runner.selectionId}
            legTypes={market.legTypes}
            marketId={market.marketId}
            marketStatus={market.marketStatus}
            marketType={market.marketType}
            odds={runner.winRunnerOdds}
            runnerResultType={runner.resultType}
            runnerStatus={runner.runnerStatus}
            selectionId={runner.selectionId}
            startTime={market.marketTime}
          />
        </div>
      </div>
      {shouldDisplayDetailsDropdown ? (
        <div
          className={classNames(styles.listItemBottomRow, {
            [styles.listItemBottomRowOpen]: isDetailsExpanded
          })}
          data-testid="runner-details-row"
        >
          <div className={styles.bottomRowContainer}>
            {shouldDisplayMoreDetailsButton ? (
              <button
                type="button"
                className={styles.moreDetailsButton}
                onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}
                aria-controls={`details-dropdown-${runner.selectionId}`}
                aria-expanded={isDetailsExpanded}
              >
                {isDetailsExpanded ? (
                  <Translation {...TXTWCL_Less_Detail} />
                ) : (
                  <Translation {...TXTCLI_More_Details} />
                )}
                <Icon
                  name="chevron-medium-down"
                  className={classNames(styles.icon, { [styles.iconOpen]: isDetailsExpanded })}
                  size="lg"
                />
              </button>
            ) : null}
            {form ? <span className={styles.form}>{`F: ${form}`}</span> : null}
          </div>
          {shouldDisplayMoreDetailsButton ? (
            <>
              <HorseDetails runner={runner} isDetailsExpanded={isDetailsExpanded} />
              {ratingStars ? (
                <div
                  className={classNames(styles.timeformVerdict, {
                    [styles.visible]: isDetailsExpanded
                  })}
                >
                  <TimeFormVerdict />
                  <StarRating rating={ratingStars} size="x2" inverted className={styles.stars} />
                  {locale.startsWith('en') ? comments : null}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </li>
  );
};

const RaceCardList: React.FC<ListProps> = ({ market, maxNumberOfRunners, templateConfig }) => {
  const { widgetSelect } = useMeetingMarketTemplatesWidget();
  const race = useSelector((state: RootState) => widgetSelect.race(state, market.raceId));

  const runners = market.runners.filter(({ selectionId }) =>
    templateConfig.runners.includes(selectionId)
  );
  const nonRunners = market.runners.filter(({ selectionId }) =>
    templateConfig.nonRunners.includes(selectionId)
  );

  const marketRunners = maxNumberOfRunners ? runners.slice(0, maxNumberOfRunners) : runners;

  return (
    <>
      <ul className={styles.list}>
        {marketRunners.map((runner) => (
          <RaceCardListItem
            key={runner.selectionId}
            runner={runner}
            market={market}
            raceCountryCode={race.countryCode}
          />
        ))}
      </ul>

      {!maxNumberOfRunners && nonRunners.length ? (
        <div className={styles.nonRunnerWrapper}>
          <div className={styles.nonRunner}>
            <Translation {...TXTWCL_Non_Runners} />
          </div>
          <ul className={styles.list}>
            {nonRunners.map((runner) => (
              <RaceCardListItem
                key={runner.selectionId}
                runner={runner}
                market={market}
                raceCountryCode={race.countryCode}
              />
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
};

export default RaceCardList;
